import type { NavLink, SiteDocument, SitePage } from "./types";
import { legalFooterLinks } from "./legal-notices";

const greekToLatin: Record<string, string> = {
  α: "a", β: "v", γ: "g", δ: "d", ε: "e", ζ: "z", η: "i", θ: "th",
  ι: "i", κ: "k", λ: "l", μ: "m", ν: "n", ξ: "x", ο: "o", π: "p",
  ρ: "r", σ: "s", ς: "s", τ: "t", υ: "y", φ: "f", χ: "ch", ψ: "ps", ω: "o"
};

export function slugify(value: string): string {
  return [...value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()]
    .map(character => greekToLatin[character] ?? character)
    .join("")
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export function pagePath(page: Pick<SitePage, "slug">): string {
  return page.slug ? `/${page.slug}` : "/";
}
/** Built-in navigation names refer to real pages when those pages exist. */
export function realPageHref(site: Pick<SiteDocument, "pages">, href: string) {
  const match = /^(?:\/)?#(services|products|about|team|contact|gallery)$/.exec(href);
  const page = match && site.pages.find(item => item.slug === match[1]);
  return page ? pagePath(page) : href;
}

/** Stable primary selection; legacy extra sites are retained, never deleted. */
export function primaryWebsite(sites: SiteDocument[], preferredId?: string): SiteDocument | undefined {
  return sites.find(site => site.id === preferredId) || [...sites].sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || "") || a.id.localeCompare(b.id))[0];
}

function uniqueLinks(links: NavLink[]): NavLink[] {
  const seen = new Set<string>();
  return links.filter(link => {
    const key = link.href || `group:${link.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Page visibility is owned by the page, while section-anchor links remain editable navigation items. */
export function orderLinks(links: NavLink[], order: string[] = []): NavLink[] {
  const positions = new Map(order.map((id, index) => [id, index]));
  return [...links].sort((a, b) => (positions.get(a.id) ?? order.length) - (positions.get(b.id) ?? order.length));
}

export function moveItem<T>(items: T[], index: number, direction: number): T[] {
  const target = index + direction;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

export function siteHeaderLinks(site: Pick<SiteDocument, "pages" | "navigation" | "header">): NavLink[] {
  const pagePaths = new Set(site.pages.map(pagePath));
  const pages = site.pages
    .filter(page => page.showInNavigation)
    .map(page => ({ id: `page-header-${page.id}`, label: page.navigationLabel || page.title, href: pagePath(page), parentId: page.navigationParentId, icon: page.navigationIcon }));
  const sectionAndCustomLinks = site.navigation.map(link => ({ ...link, href: realPageHref(site, link.href) })).filter(link => !pagePaths.has(link.href));
  return orderLinks(uniqueLinks([...pages, ...sectionAndCustomLinks]), site.header?.linkOrder);
}

export function siteFooterLinks(site: Pick<SiteDocument, "pages" | "footer"> & Partial<Pick<SiteDocument, "locale">>): NavLink[] {
  return orderLinks(uniqueLinks([...site.pages
    .filter(page => page.showInFooter ?? page.showInNavigation)
    .map(page => ({ id: `page-footer-${page.id}`, label: page.footerLabel || page.title, href: pagePath(page) })), ...legalFooterLinks(site)]), site.footer?.linkOrder);
}

export const RESERVED_SLUGS = new Set(["www", "admin", "api", "app", "mail", "support", "status", "help", "demo", "sites"]);

export function normalizeHostname(value: string): string {
  return value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}

/** Keep already-issued DNS challenges working; new Kate challenges use the current brand. */
export function domainVerificationRecordName(hostname: string, verificationToken: string): string {
  const prefix = verificationToken.startsWith("twm-") ? "_towebsitemou" : "_kate";
  return `${prefix}.${normalizeHostname(hostname)}`;
}

export function subdomainFromHost(host: string, rootDomain: string): string | null {
  const hostname = host.split(":")[0]!.toLowerCase();
  if (!hostname.endsWith(`.${rootDomain}`)) return null;
  const candidate = hostname.slice(0, -(rootDomain.length + 1));
  if (!candidate || candidate.includes(".") || RESERVED_SLUGS.has(candidate)) return null;
  return candidate;
}
